// The invitation page (#504): the link in an invitation email opens it at `/invitation#<invite code>`. It names the
// business and the Roles the person is invited to, and accepting signs them in on this device (ADR-0011). The code
// rides in the fragment, which the browser never sends to a server, so no log or Referer carries it. A used, expired
// or replaced link says so in plain words, and how to get in instead; a failure to reach LAMP (#547), too many tries or
// a PIN too easy to guess (#665) each says so instead, and leaves the invitation open to try again. The PIN, set as
// the invitation is accepted, is optional; one that is not six digits is refused here, before anything is sent (#912).
// It is kept only with the person's consent: a PIN given opens the consent message, linking the Privacy Notice, and
// declining it accepts the invitation with no PIN (#698).
import { useEffect, useState } from "react";
import { ApiError } from "../http.js";
import { useWords } from "../i18n/words.js";
import { isTooManyTries, type OpenInvitation, type PinConsentGiven, type SignInApi } from "./api.js";
import { PinConsent } from "./PinConsent.js";

export const INVITATION_PATH = "/invitation";

/** The invite code a link carries in its fragment. */
const codeInLink = (): string => decodeURIComponent(window.location.hash.replace(/^#/, "")).trim();

/** A PIN is six digits, as the Application API checks it. */
const PIN = /^[0-9]{6}$/;

export function Invitation({ signIn, onAccepted }: { signIn: SignInApi; onAccepted: () => void }) {
  const w = useWords();
  const [code] = useState(codeInLink);
  const [invitation, setInvitation] = useState<OpenInvitation | null | undefined>(undefined);
  const [trust, setTrust] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  // The PIN was given: the consent message is asked before the invitation is accepted.
  const [asking, setAsking] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (code === "") {
      setInvitation(null);
      return;
    }
    signIn.openInvitation(code).then(
      (opened) => setInvitation(opened ?? null),
      () => setProblem(w.ownerPagesUnreachable),
    );
  }, [signIn, code]);

  const accept = async (kept?: { pin: string; consent: PinConsentGiven }) => {
    setBusy(true);
    setProblem(undefined);
    try {
      await signIn.acceptInvitation(code, trust, kept?.pin, kept?.consent);
      onAccepted();
    } catch (error) {
      // Only the API's own answer that the link is used, expired or replaced closes the invitation.
      if (error instanceof ApiError && (error.status === 400 || error.status === 404)) setInvitation(null);
      else if (isTooManyTries(error)) setProblem(w.invitationTooMany);
      else if (error instanceof ApiError && error.status === 422 && error.code === "weak_pin")
        setProblem(w.invitationWeakPin);
      else setProblem(w.ownerPagesUnreachable);
      setAsking(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="sign-in" id="main">
      <h1>{w.invitationTitle}</h1>
      {invitation === undefined && !problem ? <p role="status">{w.invitationOpening}</p> : null}
      {invitation === null ? (
        <>
          <p role="alert">{w.invitationGone}</p>
          <p>
            <a className="btn btn-go" href="/">
              {w.invitationSignIn}
            </a>
          </p>
        </>
      ) : invitation?.kind === "mobile" ? (
        <p>{w.invitationTelegram}</p>
      ) : invitation ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (pin === "") void accept();
            else if (!PIN.test(pin)) setProblem(w.invitationPinSixDigits);
            else {
              setProblem(undefined);
              setAsking(true);
            }
          }}
        >
          <p>{w.invitationFor(invitation.business, invitation.roles)}</p>
          <label className="field" htmlFor="invitation-pin">
            {w.invitationPin}
          </label>
          <input
            id="invitation-pin"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={16}
            value={pin}
            onChange={(e) => setPin(e.target.value)}
          />
          <label className="check">
            <input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} />
            {w.signInTrust}
          </label>
          {asking ? null : (
            <button type="submit" className="btn btn-go" disabled={busy}>
              {w.invitationAccept}
            </button>
          )}
        </form>
      ) : null}
      {invitation && asking ? (
        <PinConsent
          busy={busy}
          decline={w.invitationNoPin}
          onConfirm={(consent) => void accept({ pin, consent })}
          onDecline={() => void accept()}
        />
      ) : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
    </main>
  );
}
